/**
 * FormA — Unified Permission Model constants
 *
 * Single source of truth for the canonical resource→verb model, mirrored by the access blob:
 *   { resource: { verb: {...scope} } }
 * Resources: nodes | plugins | discovery | activity.
 * Canonical node write verb is `edit` (the legacy `edit_content` is gone from the frontend;
 * the backend still folds it as an alias on the preview-yaml input path).
 *
 * REQUEST_TYPE_REGISTRY is keyed by the canonical "resource:verb" key (e.g. 'nodes:read').
 * Items carry {resource, verb} on the wire; the builder keeps a `request_type` field holding the
 * canonical key as an internal convenience (bridged by from/toCanonicalDlrItem). Old saved data
 * using the legacy clause names (see_nodes, edit, …) still loads via `_CLAUSE_TO_KEY`/requestTypeOf.
 */

export const RESOURCES = ['nodes', 'plugins', 'discovery', 'activity',
  'automations', 'sub_entity'];

export const VERBS_BY_RESOURCE = {
  nodes: ['read', 'edit', 'create', 'delete', 'propose'],
  // `inherit` is create's dangerous half. A `belongs_to` edge gives the
  // from-node the to-node's audience, so it publishes rather than
  // organises — one edge can make a private node readable by everyone who can
  // read some widely-shared anchor. It gets its own line on the consent card
  // as nodes:create scoped to the `belongs_to` schema, not as a verb of its own.
  plugins: ['execute', 'prepare'],
  discovery: ['friends', 'groups', 'realms'],
  activity: ['read'],
  automations: ['write', 'execute'],
  sub_entity: ['declare'],
};

/** The resources with nothing to narrow BY — the backend's SCOPELESS_RESOURCES.
 *
 * `{}` is TOP everywhere, but on these it is the ONLY form the verb has.
 * `discovery:realms` is a yes/no capability about your account, not a reach over
 * typed records; the control-plane verbs name no object at all. The server
 * rejects every scope key you could put in them, so there is no narrower version
 * of any of these to have asked for.
 *
 * Anything asking "is this unrestricted?" must ask it only of a resource that
 * COULD have been restricted — and a row from one of these carries no schema id
 * for the same reason, which is the fact that is easy to misread as "all types".
 */
export const SCOPELESS_RESOURCES = ['discovery', 'automations', 'sub_entity'];

export const VERB_LABELS = {
  'nodes.read': 'Read nodes',
  'nodes.edit': 'Edit nodes',
  'nodes.create': 'Create nodes',
  'nodes.delete': 'Delete nodes',
  'nodes.propose': 'Suggest changes (you approve first)',
  // Not a verb — a scope flag on nodes.read. Rendered as its own consent line
  // because it is a SEPARATE disclosure: "read my nodes" and "read how my
  // nodes connect" are different things, and an unrestricted read grant
  // withholds references unless this is set. It is the successor to
  // the successor to the retired relationships:read for wildcard grants.
  'nodes.include_refs': 'See how your data connects',
  'plugins.execute': 'Use tools',
  'plugins.prepare': 'Ask to run tools, for you to allow',
  'discovery.friends': 'See your friends',
  'discovery.groups': 'See your groups',
  'discovery.realms': 'Discover your realms',
  'activity.read': 'View activity log',
  'automations.write': 'Set up automations',
  'automations.execute': 'Run automations',
  'sub_entity.declare': 'Create sub-entities that act for you',
};

export const VERB_DESCRIPTIONS = {
  'nodes.read': 'Choose which nodes to share — filter by schema below',
  'nodes.edit': 'Can modify content of nodes they can see',
  'nodes.create': 'Can create new nodes in your space',
  'nodes.delete': 'Can permanently remove nodes they can see',
  'nodes.propose': 'Can suggest edits and new nodes — you approve before they apply',
  'nodes.include_refs': 'The links between your nodes are visible, not just the nodes themselves (both ends of a link must be shared for it to show)',
  'plugins.execute': 'Can use the tools of the plugins you choose below',
  'plugins.prepare': 'Can fill in a run of a tool and hand it to you to allow — it cannot start one itself',
  'discovery.friends': 'Your friend list is visible to them',
  'discovery.groups': 'Your group memberships are visible to them',
  'discovery.realms': "The names of realms you've made discoverable are visible to them",
  'activity.read': 'Can query your cross-app activity feed — what apps created, read, or modified across your data',
  'automations.write': 'Can create and manage automations that act on your data (needs a data or plugin permission to do anything)',
  'automations.execute': 'Can run automations, automatically or when something changes',
  'sub_entity.declare': 'Can declare sub-entities that act on your behalf — always bounded by the permissions you grant this app',
};

export const EMPTY_VERB_CAPS = Object.freeze({
  nodes: [],
  plugins: [],
  discovery: [],
  activity: [],
  automations: [],
  sub_entity: [],
});

export const FULL_VERB_CAPS = Object.freeze({
  nodes: ['read', 'edit', 'create', 'delete', 'propose'],
  plugins: ['execute', 'prepare'],
  discovery: ['friends', 'groups', 'realms'],
  activity: ['read'],
  automations: ['write', 'execute'],
  sub_entity: ['declare'],
});

// ── REQUEST TYPE REGISTRY ──────────────────────────────────────────────────
// Single source of truth for all permission / request / scope types.
// Keyed by the canonical "resource:verb" key. `discovery` and `schema_grants` are the two
// non-"resource:verb" keys. Every consumer imports from here.

export const REQUEST_TYPE_REGISTRY = {
  'nodes:read': {
    label: 'Read Nodes',
    description: 'Declare read access to user data by schema',
    category: 'data',
    verbs: { nodes: ['read'] },
    hasNodeFilter: true,
    triggerVerb: ['nodes', 'read'],
    scopeFilterKey: 'nodes:read',
    defaultFields: { source: 'schema', schema_ids: [], min: 0, max: null, required: false },
    scopeDefaults: { schemaIds: [], nodeIds: [] },
    scopeFilterFields: { schemaIds: 'schema_ids', nodeIds: 'node_ids' },
  },

  'nodes:edit': {
    label: 'Edit Nodes',
    description: 'Declare edit access to user data by schema',
    category: 'action',
    verbs: { nodes: ['edit'] },
    hasNodeFilter: true,
    triggerVerb: ['nodes', 'edit'],
    scopeFilterKey: 'nodes:edit',
    defaultFields: { source: 'any', min: 0, max: null, required: false },
    scopeDefaults: { schemaIds: [], nodeIds: [] },
    scopeFilterFields: { schemaIds: 'schema_ids', nodeIds: 'node_ids' },
  },
  'nodes:create': {
    label: 'Create Nodes',
    description: 'Declare ability to create nodes under a schema',
    category: 'action',
    verbs: { nodes: ['create'] },
    hasNodeFilter: true,
    triggerVerb: ['nodes', 'create'],
    scopeFilterKey: 'nodes:create',
    defaultFields: { source: 'any', min: 0, max: null, required: false },
    scopeDefaults: { schemaIds: [] },
    scopeFilterFields: { schemaIds: 'schema_ids' },
  },
  'nodes:delete': {
    label: 'Delete Nodes',
    description: 'Declare ability to delete nodes by schema',
    category: 'action',
    verbs: { nodes: ['delete'] },
    hasNodeFilter: true,
    triggerVerb: ['nodes', 'delete'],
    scopeFilterKey: 'nodes:delete',
    defaultFields: { source: 'any', min: 0, max: null, required: false },
    scopeDefaults: { schemaIds: [], nodeIds: [] },
    scopeFilterFields: { schemaIds: 'schema_ids', nodeIds: 'node_ids' },
  },
  'nodes:propose': {
    label: 'Propose Changes',
    description: 'Declare ability to propose changes for user approval',
    category: 'propose',
    verbs: { nodes: ['propose'] },
    hasNodeFilter: true,
    triggerVerb: ['nodes', 'propose'],
    scopeFilterKey: 'nodes:propose',
    defaultFields: { source: 'any', min: 0, max: null, required: false },
    scopeDefaults: { schemaIds: [], nodeIds: [] },
    scopeFilterFields: { schemaIds: 'schema_ids', nodeIds: 'node_ids' },
  },
  'plugins:execute': {
    label: 'Run Plugins',
    description: 'Declare which plugins your app will execute',
    category: 'other',
    verbs: { plugins: ['execute'] },
    hasNodeFilter: false,
    triggerVerb: ['plugins', 'execute'],
    scopeFilterKey: 'plugins:execute',
    // `tools` is the NARROWER statement and rides the same verb: per plugin, a map
    // of tool -> 'allow' | 'ask'. A provider is not a capability, so "may this app
    // use Google" is not a question anybody can answer honestly — Gmail lists,
    // reads, sends and deletes, and those deserve different answers.
    //
    // OMISSION IS DENIAL once `tools` names a plugin, or adding a tool to a
    // published plugin would silently widen every existing grant.
    defaultFields: { plugin_ids: [], tools: {} },
    scopeDefaults: { pluginIds: [], tools: {} },
    scopeFilterFields: { pluginIds: 'plugin_ids', tools: 'tools' },
  },
  'plugins:prepare': {
    label: 'Prepare Plugin Runs',
    // NOT a weaker `execute`: it is the right to compute what a run WOULD be and
    // hand the person a filled-in link to authorise. An app holding only this
    // cannot cause a run — which is the difference between an agent that helps
    // you decide and one that decides for you.
    description: 'Declare that your app will fill in plugin runs for the person to authorise',
    category: 'other',
    verbs: { plugins: ['prepare'] },
    hasNodeFilter: false,
    triggerVerb: ['plugins', 'prepare'],
    scopeFilterKey: 'plugins:prepare',
    defaultFields: { plugin_ids: [] },
    scopeDefaults: { pluginIds: [] },
    scopeFilterFields: { pluginIds: 'plugin_ids' },
  },
  discovery: {
    label: 'Discovery',
    description: 'Declare need to see your friends, groups, or the realms you make discoverable',
    category: 'other',
    verbs: { discovery: ['friends', 'groups', 'realms'] },
    hasNodeFilter: false,
    triggerVerb: ['discovery', 'friends'], // special: checked as "any discovery verb"
    scopeFilterKey: 'discovery',
    defaultFields: { friends: true, groups: true, realms: false },
    scopeDefaults: { friends: true, groups: true, realms: false },
    scopeFilterFields: { friends: 'friends', groups: 'groups', realms: 'realms' },
  },

  'activity:read': {
    label: 'Activity Log Access',
    description: 'Allow querying the cross-app activity feed — what apps have done to your data',
    category: 'advanced',
    verbs: { activity: ['read'] },
    hasNodeFilter: false,
    triggerVerb: ['activity', 'read'],
    scopeFilterKey: 'activity:read',
    defaultFields: { categories: [], actor_types: [] },
    scopeDefaults: { categories: [], actorTypes: [] },
    scopeFilterFields: { categories: 'categories', actorTypes: 'actor_types' },
  },

  // Control-plane verbs — no data scope. The coherence rule (backend) still requires the
  // grant to carry a data/plugin/agent surface for these to be meaningful.
  'automations:write': {
    label: 'Set Up Automations',
    description: 'Create and manage automations that act on your data',
    category: 'advanced',
    verbs: { automations: ['write'] },
    hasNodeFilter: false,
    triggerVerb: ['automations', 'write'],
    scopeFilterKey: 'automations:write',
    defaultFields: {},
    scopeDefaults: {},
    scopeFilterFields: {},
  },
  'automations:execute': {
    label: 'Run Automations',
    description: 'Trigger automations and let them run automatically on your behalf',
    category: 'advanced',
    verbs: { automations: ['execute'] },
    hasNodeFilter: false,
    triggerVerb: ['automations', 'execute'],
    scopeFilterKey: 'automations:execute',
    defaultFields: {},
    scopeDefaults: {},
    scopeFilterFields: {},
  },
  'sub_entity:declare': {
    label: 'Create Sub Entities',
    description: "Declare sub-entities that act within this app's permissions",
    category: 'advanced',
    verbs: { sub_entity: ['declare'] },
    hasNodeFilter: false,
    triggerVerb: ['sub_entity', 'declare'],
    scopeFilterKey: 'sub_entity:declare',
    defaultFields: {},
    scopeDefaults: {},
    scopeFilterFields: {},
  },

  // Virtual scope — not stored in verb_caps; managed via schemaGrants prop
  schema_grants: {
    label: 'Field-level Access Ceiling',
    description: 'Cap which fields and rows any share under this realm can access',
    category: 'advanced',
    verbs: {},
    hasNodeFilter: false,
    triggerVerb: null,
    scopeFilterKey: null,
    defaultFields: {},
    scopeDefaults: {},
    scopeFilterFields: {},
    virtual: true,
  },
};

// ── Canonical (resource, verb) per request kind ─────────────────────────────
// REQUEST_TYPE_CANONICAL maps each registry key -> {resource, verb}, derived from `triggerVerb`
// (the single source of truth). The legacy node write verb `edit_content` folds to `edit`.
const _canonicalVerb = (v) => (v === 'edit_content' ? 'edit' : v);
export const REQUEST_TYPE_CANONICAL = Object.fromEntries(
  Object.entries(REQUEST_TYPE_REGISTRY)
    .filter(([, def]) => Array.isArray(def.triggerVerb) && def.triggerVerb.length === 2)
    .map(([rt, def]) => [rt, { resource: def.triggerVerb[0], verb: _canonicalVerb(def.triggerVerb[1]) }])
);

// Reverse "resource:verb" -> registry key (identity-ish since the registry IS canonical-keyed).
export const REQUEST_TYPE_BY_CANONICAL = Object.fromEntries(
  Object.entries(REQUEST_TYPE_CANONICAL).map(([rt, rv]) => [`${rv.resource}:${rv.verb}`, rt])
);

// Legacy clause name -> canonical registry key. Fallback only for items that still carry a legacy
// `request_type`/`type` string (old saved data); canonical resource+verb items resolve directly.
const _CLAUSE_TO_KEY = {
  see_nodes: 'nodes:read',
  edit: 'nodes:edit',
  create: 'nodes:create',
  create_nodes: 'nodes:create',
  delete_nodes: 'nodes:delete',
  propose: 'nodes:propose',
  run_plugins: 'plugins:execute',
  discover: 'discovery',
  see_activity: 'activity:read',
  schema_grants: 'schema_grants',
};

/** The canonical registry key for an item, whether it stores resource+verb or a legacy clause. */
export function requestTypeOf(item) {
  // Discovery is a resource with no single verb — a discovery item carries resource:'discovery'.
  if (item.resource === 'discovery') return 'discovery';
  if (item.resource && item.verb) return `${item.resource}:${item.verb}`;
  const legacy = item.request_type || item.type;
  if (legacy) {
    // Already a canonical registry key? return it; otherwise map the legacy clause name.
    if (REQUEST_TYPE_REGISTRY[legacy]) return legacy;
    return _CLAUSE_TO_KEY[legacy] || null;
  }
  return null;
}

/** A node item's source ('any' | 'schema') — canonical items drop `source`, so derive it:
 * present schema scope ⇒ 'schema', absent ⇒ 'any' (matches normalize_inputs's absence==any). */
export function sourceOf(item) {
  if (item.source) return item.source;
  return (item.schema_ids && item.schema_ids.length) ? 'schema' : 'any';
}

/** Translate a (possibly legacy clause) item to a canonical resource+verb item for emit. */
export function toCanonicalDlrItem(item) {
  const rv = item.resource && item.verb
    ? { resource: item.resource, verb: item.verb }
    : REQUEST_TYPE_CANONICAL[requestTypeOf(item)];
  const out = { ...item };
  delete out.request_type; delete out.type; delete out.source;
  if (rv) { out.resource = rv.resource; out.verb = rv.verb; }
  // node scope: source:'any' -> unrestricted (no schemas); else keep schema_ids as `schemas`.
  if (rv && rv.resource === 'nodes') {
    if (item.source === 'any') { delete out.schema_ids; delete out.schemas; }
    else if (item.schema_ids && !out.schemas) { out.schemas = item.schema_ids; delete out.schema_ids; }
  }
  // nodes:read may carry include_refs — "unrestricted, AND show me the links".
  // Without this the builder drops it on save and the grant silently narrows.
  if (rv && rv.resource === 'nodes' && rv.verb === 'read' && item.include_refs) {
    out.include_refs = true;
  }
  return out;
}

/** Translate a canonical resource+verb item back to the builder's clause shape (load side),
 * so existing apps (now stored canonically) still open in the request builder. */
export function fromCanonicalDlrItem(item) {
  if (!item.resource || !item.verb) return item;            // already clause form
  const rt = REQUEST_TYPE_BY_CANONICAL[`${item.resource}:${item.verb}`];
  const out = { ...item, request_type: rt };
  delete out.resource; delete out.verb;
  if (item.resource === 'nodes') {
    if (item.schemas) { out.schema_ids = item.schemas; out.source = 'schema'; delete out.schemas; }
    else { out.source = 'any'; }
  }
  if (item.resource === 'nodes' && item.include_refs) out.include_refs = true;
  return out;
}

// Derived arrays — add new types to the registry, these update automatically
export const VALID_REQUEST_TYPES = Object.keys(REQUEST_TYPE_REGISTRY);

export const NODE_FILTER_TYPES = Object.entries(REQUEST_TYPE_REGISTRY)
  .filter(([, def]) => def.hasNodeFilter)
  .map(([key]) => key);

// Menu groupings for DLR builder "Add Request" dropdown
export const REQUEST_MENU = [
  { group: 'Data Access', types: ['nodes:read'] },
  { group: 'Actions', types: ['nodes:edit', 'nodes:create', 'nodes:delete', 'nodes:propose'] },
  { group: 'Other', types: ['plugins:execute', 'discovery'] },
  { group: 'Advanced', types: ['activity:read'] },
  { group: 'Automation & Sub Entities', types: ['automations:write', 'automations:execute', 'sub_entity:declare'] },
];

// Category colors for DLR cards and FormA scopes
export const CATEGORY_COLORS = {
  data:    { bg: 'rgba(59,130,246,0.12)',  color: '#3b82f6', border: 'rgba(59,130,246,0.25)' },
  action:  { bg: 'rgba(245,158,11,0.12)',  color: '#d97706', border: 'rgba(245,158,11,0.25)' },
  propose: { bg: 'rgba(16,185,129,0.10)',  color: '#059669', border: 'rgba(16,185,129,0.22)' },
  other:   { bg: 'rgba(139,92,246,0.12)',  color: '#7c3aed', border: 'rgba(139,92,246,0.25)' },
  advanced:{ bg: 'rgba(239,68,68,0.10)',   color: '#dc2626', border: 'rgba(239,68,68,0.22)' },
};

// Human-readable labels for category badges on scope cards
export const CATEGORY_DISPLAY_LABELS = {
  data:     'READ',
  action:   'WRITE',
  propose:  'PROPOSE',
  other:    'ACTION',
  advanced: 'ADVANCED',
};

// Fixed enum options for the activity:read scope card
export const ACTIVITY_CATEGORIES = [
  { id: 'node',         name: 'Node changes' },
  { id: 'app_access',   name: 'App data access' },
  { id: 'data_read',    name: 'Data reads' },
  { id: 'security',     name: 'Security events' },
  { id: 'sharing',      name: 'Sharing events' },
  { id: 'plugin',       name: 'Plugin executions' },
  { id: 'automation',   name: 'Automations' },
  { id: 'relationship', name: 'Relationship changes' },
];

export const ACTIVITY_ACTOR_TYPES = [
  { id: 'third_party', name: 'Third-party apps' },
  { id: 'user',        name: 'Your direct actions' },
  { id: 'system',      name: 'System' },
];

// Resource display labels (used by SharingPreview, etc.)
/** One label per resource in RESOURCES. `activity` was missing, so every screen
 *  that reads this fell through to the raw key and headed the section "activity"
 *  in lower case beside six title-cased siblings — in three components, because
 *  they all guard with `|| resource` and none of them noticed. */
export const RESOURCE_LABELS = {
  nodes: 'Nodes',
  plugins: 'Plugins',
  discovery: 'Discovery',
  activity: 'Activity',
  automations: 'Automations',
  sub_entity: 'Sub Entities',
};

// Node source options for DLR node filter fields
export const NODE_SOURCE_OPTIONS = [
  { value: 'any', label: 'Any node' },
  { value: 'schema', label: 'Specific schema' },
];

// ── Utility functions ─────────────────────────────────────────────────────

/**
 * Ensure a DLR item has a `schema_ids` array.
 */
export function normalizeDlrSchemas(item) {
  if (item.schema_ids) return item;
  return { ...item, schema_ids: [] };
}

/**
 * Create a default DLR request item for a given type (canonical registry key).
 * Emits the canonical {resource, verb} shape; discovery carries just the resource.
 */
export function makeDefaultRequest(requestType) {
  const def = REQUEST_TYPE_REGISTRY[requestType];
  if (!def) return { key: '', label: requestType, description: '', request_type: requestType };
  const rv = REQUEST_TYPE_CANONICAL[requestType];
  const base = { key: '', label: def.label, description: '', ...(def.defaultFields || {}) };
  if (rv) return { ...base, resource: rv.resource, verb: rv.verb };
  if (requestType === 'discovery') return { ...base, resource: 'discovery' };
  return { ...base, request_type: requestType };
}

/**
 * Convert verb_caps object to structured DLR input items (canonical {resource, verb}).
 */
export function migrateDlrVerbsToInputs(verbCaps) {
  if (!verbCaps || typeof verbCaps !== 'object') return [];
  const items = [];
  for (const [type, def] of Object.entries(REQUEST_TYPE_REGISTRY)) {
    if (!def.triggerVerb) continue; // virtual entries (e.g. schema_grants)
    if (type === 'discovery') {
      if (Array.isArray(verbCaps.discovery) && verbCaps.discovery.length > 0) {
        items.push({
          key: 'social_discovery', label: def.label, description: '', resource: 'discovery',
          friends: verbCaps.discovery.includes('friends'),
          groups: verbCaps.discovery.includes('groups'),
          realms: verbCaps.discovery.includes('realms'),
        });
      }
      continue;
    }
    const [resource, verb] = def.triggerVerb;
    if (Array.isArray(verbCaps[resource]) && verbCaps[resource].includes(verb)) {
      const rv = REQUEST_TYPE_CANONICAL[type];
      items.push({ key: type, label: def.label, description: '', resource: rv.resource, verb: rv.verb, ...(def.defaultFields || {}) });
    }
  }
  return items;
}

/**
 * Merge existing DLR inputs with verbs not yet covered (for edit flows).
 * Ensures verbs from requested_verbs that don't have a DLR item get one.
 */
export function migrateAndMergeInputs(inputs, requestedVerbs) {
  const existing = inputs || [];
  const coveredTypes = new Set(existing.map(i => requestTypeOf(i)));
  const extras = migrateDlrVerbsToInputs(requestedVerbs || {});
  const merged = [...existing];
  for (const item of extras) {
    if (!coveredTypes.has(requestTypeOf(item))) {
      merged.push(item);
    }
  }
  return merged;
}

/**
 * Derive verb_caps from a list of DLR request items.
 */
export function deriveVerbsFromRequests(requests) {
  const verbs = { nodes: new Set(), plugins: new Set(), discovery: new Set(), activity: new Set(), automations: new Set(), sub_entity: new Set() };
  for (const req of (requests || [])) {
    const rt = requestTypeOf(req) || 'nodes:read';
    const def = REQUEST_TYPE_REGISTRY[rt];
    if (!def) continue;
    if (rt === 'discovery') {
      // Two spellings. Stored items are canonical — one item per verb, `verb: realms` —
      // and the server unions that verb with any flag set to true. Reading the flags'
      // DEFAULTS on a canonical item turned a realms-only ask into friends + groups
      // and lost the realms. The flag defaults apply only to the legacy flags-only item.
      if (req.verb) {
        verbs.discovery.add(req.verb);
        for (const v of ['friends', 'groups', 'realms']) if (req[v] === true) verbs.discovery.add(v);
        continue;
      }
      if (req.friends !== false) verbs.discovery.add('friends');
      if (req.groups !== false) verbs.discovery.add('groups');
      if (req.realms === true) verbs.discovery.add('realms');
      continue;
    }
    for (const [resource, verbList] of Object.entries(def.verbs)) {
      for (const v of verbList) verbs[resource].add(v);
    }
  }
  return {
    nodes: [...verbs.nodes],
    plugins: [...verbs.plugins],
    discovery: [...verbs.discovery],
    activity: [...verbs.activity],
    automations: [...verbs.automations],
    sub_entity: [...verbs.sub_entity],
  };
}

/**
 * Build a data_lender_request from a form contract (dlr_inputs + optional input_parameters).
 * Shared between DeveloperDashboard (register) and MyAppsTab (edit). Emits canonical items.
 */
export function buildDLRFromContract(form) {
  const inputs = form.dlr_inputs || [];
  return {
    requested_verbs: deriveVerbsFromRequests(inputs),
    dlr_inputs: inputs.map(toCanonicalDlrItem),
  };
}

// Variable types for DLR definitions
export const VARIABLE_TYPES = ['node', 'schema', 'static'];

// Realm-context descriptions for scope cards (keyed by the canonical 'resource:verb' = the
// REQUEST_TYPE_REGISTRY key / scope.type).
export const REALM_CLAUSE_DESCRIPTIONS = {
  'nodes:read':            'Controls which nodes are visible in this realm. Filters below narrow it further.',
  'nodes:edit':            'Allows modifying node content. Filters below restrict which nodes can be edited.',
  'nodes:create':          'Allows creating new nodes. Restrict by schema below.',
  'nodes:delete':          'Allows permanently deleting nodes. This cannot be undone.',
  'nodes:propose':         'Allows suggesting edits — you approve before any change takes effect.',
  'plugins:execute':       'Allows running plugins on data in this realm.',
  'discovery':             'Makes your social graph visible (friends list and/or group memberships).',
  'activity:read':         'Grants read access to your cross-app activity log.',
};

